import { Component, OnInit, OnDestroy, ViewChild, AfterViewInit } from '@angular/core';
import { NavigationEnd, NavigationStart, Router, RouterOutlet } from '@angular/router';
import { CdkVirtualScrollViewport } from '@angular/cdk/scrolling';

import { BehaviorSubject, Observable, Subject, Subscription, merge, of } from 'rxjs';
import { map, tap, startWith, switchMap, takeUntil, catchError, filter, take, skip } from 'rxjs/operators';

import { SearchService, BreakpointService } from '@library/core/services';
import { AddressbookService } from './addressbook.service';

import { Business } from '@library/common/models';
import { MatSnackBar } from '@angular/material/snack-bar';

const AlphaMap = () => new Map([
   [ '+', -1 ],
   ...Array.from({ length: 26 }, ( _, i ) => String.fromCharCode( 65 + i ))
      .map( char => [ char, 0 ] as [ string, number ])
]);

@Component( {
   selector: 'app-addressbook',
   templateUrl: './addressbook.component.html',
   styleUrls: [ './addressbook.scss' ],
} )
export class AddressBookComponent implements OnInit, OnDestroy, AfterViewInit {
   ab$: Observable<Business[]>;

   get filterTerm() {
      return ( this.search.params.get( 'text' ).value || '' ).toLowerCase().trim();
   }

   @ViewChild('entryOutlet') entryOutlet!: RouterOutlet;
   @ViewChild(CdkVirtualScrollViewport) set viewport( vp: CdkVirtualScrollViewport) {
      if ( vp != null ) {
         this._vpLoaded.next( vp );
      }
   };
   _vpLoaded = new BehaviorSubject<CdkVirtualScrollViewport>( null );

   _onDestroy: Subject<void> = new Subject();

   AlphaMap = AlphaMap();

   sideClicked = false;

   routerEventsSub: Subscription;
   nextUrl: string;
   scrollSub: Subscription;

   constructor(
      private abService: AddressbookService,
      private search: SearchService,
      private snackBar: MatSnackBar,
      private router: Router,

      public breakpoints: BreakpointService,
   ) {
      this.routerEventsSub = this.router.events.subscribe(event => {
         if (event instanceof NavigationStart) {
           this.nextUrl = event.url;
         }
       });
   }

   ngOnInit() {
      this.search.showSearch = true;

      this.ab$ = merge(
         this.search.params.get( 'text' ).valueChanges,
         this.abService.refresh$,
         this.abService.filter$,
      ).pipe(
         takeUntil( this._onDestroy ),
         startWith( true ),
         switchMap( _ => this.abService.getAddressBook() ),
         map( book => {
            const type = this.abService.filter$.getValue();
            
            let b = book;

            if ( type != 'all' ) {
               b = book.filter((a) => a.type === type );
            }

            this.AlphaMap.clear();
            this.AlphaMap = AlphaMap();

            // filter to anything that matches the filter term
            if ( !this.filterTerm.length ) { return b; }
            
            return b.filter( a => this.search.objectToString( a ).includes( this.filterTerm ));
         }),
         // tap( _ => this.search.showSearch = true ),
         // Set AlphaMap
         tap( book =>
            book.map( b => b.name[0]?.toUpperCase() )
               .map(( char, index ) => {
                  if ( !this.AlphaMap.get( char )) {
                     this.AlphaMap.set( char, index + 1 );
                  }
               })
         ),
         catchError( () => {
            this.snackBar.open('The system was unable to load your address book.  We apologize for the inconvience, please try again later.', 'OK');

            return of( [] );
         } ),
      );

      let vp: CdkVirtualScrollViewport = null;

      const sub = this._vpLoaded.pipe(
         filter(( vp ) => vp != null),
         take( 1 ),
         tap( _vp => { vp = _vp; } ),
         switchMap( _ => this.ab$.pipe( take( 1 ) ) ),
         tap( books => {
            let id = null;
            
            const url = this.router.url;
            const regex = /\/addressbook\/([^\/]+)(?:\/(\d+))?/; // match URL to `/addressbook/*/<number>`
            const match = url.match( regex );
            
            if ( match ) {
               id = +match[2] || null;
            }
            
            const currentIndex = id == null ? 0 : books.findIndex(( b ) => +b.id === +id );

            setTimeout(() => {
               vp.scrollToIndex( currentIndex );
               sub.unsubscribe();
            }, 200);
         })
      ).subscribe();

      this.sideClicked = false;
   }

   ngOnDestroy() {
      this._onDestroy.next();
      this._onDestroy.complete();
      this.scrollSub.unsubscribe();

      if ( !this.nextUrl.includes( '/addressbook' ) ) {
         this.abService.changeFilter('all');
      }

      this.routerEventsSub.unsubscribe();
   }

   ngAfterViewInit(): void {
      this.scrollToItem();
   }

   scrollToItem( ) {
      let id = null;
      let currentIndex = -1;

      this.scrollSub = merge(
         this.router.events.pipe(
            filter((e): e is NavigationEnd => e instanceof NavigationEnd)),
         this.abService.filter$,
      ).pipe(
         tap( e => {
            if ( e instanceof NavigationEnd ) {
               const regex = /\/addressbook\/([^\/]+)(?:\/(\d+))?/; // match URL to `/addressbook/*/<number>`
               const match = e.urlAfterRedirects.match( regex );

               if ( match ) {
                  id = +match[2] || null;
               }
            }
         }),
         switchMap( _ => this.ab$.pipe( take( 1 ) ) ),
         tap( books => currentIndex = id == null ? 0 : books.findIndex(( b ) => +b.id === +id ) ),
         switchMap( _ => this._vpLoaded.pipe( skip( 1 ), take( 1 ) ) ),
      ).subscribe(( vp ) => {
         if ( currentIndex >= 0 && !this.sideClicked ) {
            setTimeout(() => {
               vp.scrollToIndex( currentIndex );
            }, 100);
         }
         this.sideClicked = false;
      });
   }

   listClick( ) {
      this.sideClicked = true;
   }
}
